import React, { useCallback } from "react";

import { useParams } from "react-router-dom";

import { useGetTransitionDetailsDto } from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useGetTransitionDetailsDto";
import useTransitionProgress from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useTransitionProgress";

import { selectFirstTransitionAdvisor } from "../../utils";
import { useRetry } from "../hooks/useRetry";
import TransitionErrorState from "../ui/TransitionErrorState";
import TransitionProgressIndicator from "../ui/TransitionProgressIndicator";
import {
  type CompanyInfo,
  createProgressStep,
} from "../ui/TransitionProgressIndicator.types";
import TransitionProgressIndicatorSkeleton from "../ui/TransitionProgressIndicatorSkeleton";
import TransitionProgressInfo from "../ui/TransitionProgressInfo";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

const TransitionProgress: React.FC = () => {
  const auth = useAdvisorRequestAuth();
  const { transitionId } = useParams<{ transitionId: string }>();
  const { handleRetry } = useRetry("tasks");

  const progressResult = useTransitionProgress(transitionId!, auth);
  const transitionDetailsResult = useGetTransitionDetailsDto(
    transitionId,
    auth
  );

  const renderContent = useCallback(() => {
    if (progressResult.isLoading) {
      return <TransitionProgressIndicatorSkeleton />;
    }

    if (progressResult.hasError) {
      return (
        <TransitionErrorState
          message="Could not load transition progress"
          onRetry={handleRetry}
        />
      );
    }

    const { data } = progressResult;

    const progressSteps = data.sections.map((section) =>
      createProgressStep(
        section.title,
        section.completedSubtasks,
        section.totalSubtasks
      )
    );

    const progressInfo = (
      <TransitionProgressInfo
        completedTasks={data.completedTasks}
        totalTasks={data.totalTasks}
        showPercentage
      />
    );

    const companyInfo: CompanyInfo | undefined = transitionDetailsResult.data
      ?.advisors.length
      ? {
          teamName: transitionDetailsResult.data.team || "",
          advisorName:
            selectFirstTransitionAdvisor(transitionDetailsResult.data.advisors)
              ?.fullName || "",
        }
      : undefined;

    return (
      <TransitionProgressIndicator
        steps={progressSteps}
        progressInfo={progressInfo}
        companyInfo={companyInfo}
      />
    );
  }, [handleRetry, progressResult, transitionDetailsResult]);

  return <div>{renderContent()}</div>;
};

export default TransitionProgress;
